export interface TreatmentCategoryApiResource {
  id: string;
  code: string;
  name_en: string;
  name_pl: string;
  color: string;
  is_active: boolean;
  treatment_count: number;
  image_url: string | null;
}

export interface BodyAreaApiResource {
  id: string;
  code: string;
  name_en: string;
  name_pl: string;
  treatment_count: number;
  image_url: string | null;
}

export interface SettingFieldOptionApiResource {
  label_en: string;
  label_pl: string;
  label: string;
}

export interface SettingFieldApiResource {
  id: string;
  name_en: string;
  name_pl: string;
  name: string;
  unit: string | null;
  field_type: string;
  options: SettingFieldOptionApiResource[];
}

export interface DeviceApiResource {
  id: string;
  name_en: string;
  name_pl: string;
  is_active: boolean;
  treatment_count: number;
  setting_fields: SettingFieldApiResource[];
  image_url: string | null;
}

export interface DeviceBriefApiResource {
  id: string;
  name_en: string;
  name_pl: string;
  setting_fields: SettingFieldApiResource[];
}

export interface TreatmentApiResource {
  id: string;
  name_en: string;
  name_pl: string;
  category: { id: string; name_en: string; name_pl: string; color: string } | null;
  device: DeviceBriefApiResource | null;
  default_duration_seconds: number | null;
  default_duration_unit: string | null;
  default_duration_legacy_text: string | null;
  contraindication: string | null;
  body_areas: { id: string; name_en: string; name_pl: string }[];
  is_active: boolean;
  usage_count: number;
  patient_count: number;
  image_url: string | null;
}

export interface TreatmentCategory {
  id: string;
  code: string;
  nameEn: string;
  namePl: string;
  color: string;
  isActive: boolean;
  treatmentCount: number;
  imageUrl: string | null;
}

export interface BodyArea {
  id: string;
  code: string;
  nameEn: string;
  namePl: string;
  treatmentCount: number;
  imageUrl: string | null;
}

export interface SettingFieldOption {
  labelEn: string;
  labelPl: string;
  label: string;
}

export interface SettingField {
  id: string | null;
  nameEn: string;
  namePl: string;
  name: string;
  unit: string;
  fieldType: string;
  options: SettingFieldOption[];
}

export interface DeviceBrief {
  id: string;
  nameEn: string;
  namePl: string;
  settingFields: SettingField[];
}

export interface Device {
  id: string;
  nameEn: string;
  namePl: string;
  isActive: boolean;
  treatmentCount: number;
  settingFields: SettingField[];
  imageUrl: string | null;
}

export interface TreatmentCategoryBrief {
  id: string;
  nameEn: string;
  namePl: string;
  color: string;
}

export interface Treatment {
  id: string;
  nameEn: string;
  namePl: string;
  category: TreatmentCategoryBrief | null;
  device: DeviceBrief | null;
  defaultDurationSeconds: number | null;
  defaultDurationUnit: string | null;
  defaultDurationLegacyText: string | null;
  contraindication: string | null;
  bodyAreas: { id: string; nameEn: string; namePl: string }[];
  isActive: boolean;
  usageCount: number;
  patientCount: number;
  imageUrl: string | null;
}

export interface CategoryDraft {
  id: string | null;
  nameEn: string;
  namePl: string;
  color: string;
}

export interface BodyAreaDraft {
  id: string | null;
  nameEn: string;
  namePl: string;
}

export interface SettingFieldOptionDraft {
  labelEn: string;
  labelPl: string;
}

export interface SettingFieldDraft {
  nameEn: string;
  namePl: string;
  unit: string;
  fieldType: string;
  options: SettingFieldOptionDraft[];
}

export interface DeviceWritePayload {
  nameEn: string;
  namePl: string;
  settingFields: SettingFieldDraft[];
  isActive: boolean;
}

export interface TreatmentWritePayload {
  nameEn: string;
  namePl: string;
  categoryId: string | null;
  deviceId: string | null;
  defaultDurationSeconds: number | null;
  defaultDurationUnit: string | null;
  contraindication: string | null;
  bodyAreaIds: string[];
  isActive: boolean;
}

function mapSettingFieldOption(resource: SettingFieldOptionApiResource): SettingFieldOption {
  return { labelEn: resource.label_en, labelPl: resource.label_pl, label: resource.label };
}

function mapSettingField(resource: SettingFieldApiResource): SettingField {
  return {
    id: resource.id,
    nameEn: resource.name_en,
    namePl: resource.name_pl,
    name: resource.name,
    unit: resource.unit === null ? '' : resource.unit,
    fieldType: resource.field_type,
    options: resource.options.map(mapSettingFieldOption),
  };
}

export function mapApiResourceToCategory(resource: TreatmentCategoryApiResource): TreatmentCategory {
  return {
    id: resource.id,
    code: resource.code,
    nameEn: resource.name_en,
    namePl: resource.name_pl,
    color: resource.color,
    isActive: resource.is_active,
    treatmentCount: resource.treatment_count,
    imageUrl: resource.image_url,
  };
}

export function mapApiResourceToBodyArea(resource: BodyAreaApiResource): BodyArea {
  return {
    id: resource.id,
    code: resource.code,
    nameEn: resource.name_en,
    namePl: resource.name_pl,
    treatmentCount: resource.treatment_count,
    imageUrl: resource.image_url,
  };
}

export function mapApiResourceToDevice(resource: DeviceApiResource): Device {
  return {
    id: resource.id,
    nameEn: resource.name_en,
    namePl: resource.name_pl,
    isActive: resource.is_active,
    treatmentCount: resource.treatment_count,
    settingFields: resource.setting_fields.map(mapSettingField),
    imageUrl: resource.image_url,
  };
}

function mapDeviceBrief(resource: DeviceBriefApiResource | null): DeviceBrief | null {
  if (resource === null) {
    return null;
  }
  return {
    id: resource.id,
    nameEn: resource.name_en,
    namePl: resource.name_pl,
    settingFields: resource.setting_fields.map(mapSettingField),
  };
}

export function mapApiResourceToTreatment(resource: TreatmentApiResource): Treatment {
  return {
    id: resource.id,
    nameEn: resource.name_en,
    namePl: resource.name_pl,
    category:
      resource.category === null
        ? null
        : {
            id: resource.category.id,
            nameEn: resource.category.name_en,
            namePl: resource.category.name_pl,
            color: resource.category.color,
          },
    device: mapDeviceBrief(resource.device),
    defaultDurationSeconds: resource.default_duration_seconds,
    defaultDurationUnit: resource.default_duration_unit,
    defaultDurationLegacyText: resource.default_duration_legacy_text,
    contraindication: resource.contraindication,
    bodyAreas: resource.body_areas.map(function toArea(area) {
      return { id: area.id, nameEn: area.name_en, namePl: area.name_pl };
    }),
    isActive: resource.is_active,
    usageCount: resource.usage_count,
    patientCount: resource.patient_count,
    imageUrl: resource.image_url,
  };
}

export function mapDeviceWritePayload(payload: DeviceWritePayload): Record<string, unknown> {
  return {
    name_en: payload.nameEn,
    name_pl: payload.namePl,
    is_active: payload.isActive,
    setting_fields: payload.settingFields.map(function toField(field) {
      return {
        name_en: field.nameEn,
        name_pl: field.namePl,
        unit: field.unit,
        field_type: field.fieldType,
        options: field.options.map(function toOption(option) {
          return { label_en: option.labelEn, label_pl: option.labelPl };
        }),
      };
    }),
  };
}

export function mapTreatmentWritePayload(payload: TreatmentWritePayload): Record<string, unknown> {
  return {
    name_en: payload.nameEn,
    name_pl: payload.namePl,
    category_id: payload.categoryId,
    device_id: payload.deviceId,
    default_duration_seconds: payload.defaultDurationSeconds,
    default_duration_unit: payload.defaultDurationUnit,
    contraindication: payload.contraindication,
    body_area_ids: payload.bodyAreaIds,
    is_active: payload.isActive,
  };
}
